import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { FormikErrors, FormikHelpers, FormikValues } from 'formik';
import GeneralPageLayout from '../../../../components/layout/GeneralPageLayout';
import { ControlledPaymentMethodSelector as PaymentMethodSelector } from '../../../../components/payment/common/PaymentMethodSelector';
import {
  CustomPaymentData,
  CustomPaymentForm,
  CustomPaymentFormElements,
} from '../../../../components/payment/custom-form';
import {
  ACCTYPE,
  CardConnectApiData,
  CardConnectPaymentType as PaymentType,
} from '../../../../types/payment';
import { PAYMENT_METHODS } from '../../../../common/enums';
import { FormValidations } from '../../../../components/form/constants';
import { useCloseWindow } from '../../../../hooks/useCloseWindow';
import { cardConnectPostback } from '../../../../api/payment';
import { useAppDispatch } from '../../../../store/store';
import { selectCurrentFlow } from '../../../../store/slices/flow/flowSelectors';
import { selectPayment } from '../../../..//store/slices/payment/paymentSelectors';
import { checkPaymentAsync } from '../../../../store/slices/payment/paymentThunk';
import { resolveSuccessUrl } from '../../../../common/routes';
import usePlaceNavigate from 'src/hooks/usePlaceNavigate';
import { convertNumberToCurrency } from 'src/common/utils';
import { selectConfigSettings } from 'src/store/slices/config/configSelectors';

const IFRAME_PARAMS = {
  placeholdercvv: 'CVV',
  invalidinputevent: 'true',
  invalidcreditcardevent: 'true',
  invalidcvvevent: 'true',
  invalidexpiryevent: 'true',
  enhancedresponse: 'true',
  css: `
    body{
      margin:0;
    }
    label{
      color:#575757;
      font-family:PingFang SC, sans-serif;
      font-size:14px;
      font-weight:normal;
      font-smoothing:antialiasing;
      margin:4px 0;
    }
    input, select{
      border:1px solid #E5E5EA;
      border-radius:6px;
      box-sizing:border-box;
      font-size:14px;
      color:#575757;
      padding:7px 11px;
      height:36px;
    }
    input{
      width:100%;
    }
  `,
};

const accountTypeData = [
  { label: 'Checking', value: ACCTYPE.CHECKING },
  { label: 'Savings', value: ACCTYPE.SAVINGS },
];

interface CardConnectFormValues {
  accttype?: ACCTYPE;
}

export const CardConnect = () => {
  const dispatch = useAppDispatch();
  const placeNavigate = usePlaceNavigate();
  const { search } = useLocation();
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const amountNumber = Number(params.get('amount')) || 0;
  const amount = convertNumberToCurrency(amountNumber);
  const rawFees = Number(params.get('fees')) || 0;
  const fees = convertNumberToCurrency(rawFees);
  const payment = useSelector(selectPayment);
  const paymentId = params.get('paymentId') as string;
  const settings = useSelector(selectConfigSettings);
  const place = settings?.place as string;

  const currentFlow = useSelector(selectCurrentFlow);

  const [error, setError] = useState<string>();
  const [token, setToken] = useState<string>();
  const [errorInData, setErrorInData] = useState<string | null>(null);
  useEffect(() => {
    if (!paymentId || !amountNumber || !place) {
      setErrorInData('Not enough information to process the payment.');
    }
  }, [paymentId, amountNumber, place]);

  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(
    PAYMENT_METHODS.CREDIT_OR_DEBIT__CARD
  );

  const paymentType = useMemo(() => {
    if (paymentMethod === PAYMENT_METHODS.ACH_DIRECT_DEBIT) {
      return PaymentType.ACH;
    }
    return PaymentType.CC;
  }, [paymentMethod]);

  useEffect(() => {
    const updateToken = (event: { origin: string; data: string }) => {
      // This verification is needed as it can cause to colluide with other messages
      if (/^http[s]+:\/\/.+.cardconnect.com/g.test(event.origin)) {
        const data = JSON.parse(event.data);
        setError(data.validationError);
        if (!data.validationError) {
          setToken(data.message);
        } else {
          setToken(undefined);
        }
      }
    };
    window.addEventListener('message', updateToken, false);
    return () => {
      window.removeEventListener('message', updateToken);
    };
  }, []);

  const onSubmit = useCallback(
    (values: CardConnectFormValues & CustomPaymentData) => {
      const postTransaction = async (
        values: CardConnectFormValues & CustomPaymentData
      ) => {
        if (!token) return;
        setLoading(true);
        let paymentType = PaymentType.CC;
        if (paymentMethod === PAYMENT_METHODS.ACH_DIRECT_DEBIT) {
          paymentType = PaymentType.ACH;
        }
        const { postal_code, first_name, last_name, ...data } = values;
        const body: CardConnectApiData = {
          token,
          country: 'US',
          ...data,
          paymentId,
          // Account type is not required for credit cards
          accttype: paymentType === 'CC' ? undefined : values.accttype,
          paymentType,
          name: `${first_name} ${last_name}`,
          postal: postal_code,
        };
        try {
          const result = await cardConnectPostback(body);
          if (
            result.data.status === 'success' ||
            result.data.status === 'pending'
          ) {
            placeNavigate(`/payment-completed`);
          } else {
            placeNavigate(`/payment-error`);
            console.error('Error executing the payment', result);
          }
        } catch (err: unknown) {
          console.error(err);
          setError(typeof err === 'string' ? err : String(err));
        } finally {
          setLoading(false);
        }
      };
      postTransaction(values);
    },
    [token, currentFlow, paymentId, paymentMethod, placeNavigate, dispatch]
  );

  const iframeUrl = useMemo(() => {
    if (!payment.formData) {
      setErrorInData(
        'There was an error loading the data for the payment. Close the tab and try again.'
      );
      return '';
    }
    const tokenizer = (payment.formData as { tokenizer: string }).tokenizer;
    const isCC = paymentMethod === PAYMENT_METHODS.CREDIT_OR_DEBIT__CARD;
    const params: Record<string, string> = {
      ...IFRAME_PARAMS,
      useexpiry: isCC.toString(),
      usecvv: isCC.toString(),
      placeholder: isCC ? 'Card' : 'RoutingNumber/AccountNumber',
      fullmobilekeyboard: (!isCC).toString(),
    };

    const url = new URL(tokenizer);
    url.search = new URLSearchParams(params).toString();
    return url;
  }, [paymentMethod, payment.formData]);

  const paymentDetailsInfo = useMemo(() => {
    if (paymentMethod !== PAYMENT_METHODS.CREDIT_OR_DEBIT__CARD) {
      return (
        <>
          <p>
            Please enter your ACH routing number and account number into the box
            below, separated by a /{' '}
          </p>
          <p>e.g. 123456789/1234123412341234</p>
        </>
      );
    }
  }, [paymentMethod]);

  const formValidation = useCallback(
    (values: CardConnectFormValues): FormikErrors<CardConnectFormValues> => {
      const errors: FormikErrors<CardConnectFormValues> = {};
      if (!values.accttype && paymentType !== PaymentType.CC) {
        errors.accttype = FormValidations.ERROR_REQUIRED;
      }
      return {};
    },
    [paymentType]
  );

  const { isAbleToCloseWindow, closeWindow } = useCloseWindow();

  if (errorInData) {
    return (
      <GeneralPageLayout
        showNext={isAbleToCloseWindow}
        next="Close Window"
        onNext={closeWindow}
      >
        {errorInData}
      </GeneralPageLayout>
    );
  }

  return (
    <GeneralPageLayout>
      <CustomPaymentForm
        error={error}
        heading={`Amount Due: ${amount}`}
        subHeading={
          rawFees > 0 ? `Included Payment Processing Fees: ${fees}` : ''
        }
        loading={loading}
        onSubmit={onSubmit}
        submitText={`Pay ${amount}`}
        additionalInitialValues={{ accttype: undefined }}
        additionalValidation={formValidation}
      >
        {(
          formik: FormikHelpers<FormikValues & CardConnectFormValues> & {
            values: FormikValues & CardConnectFormValues;
          }
        ) => {
          const setAccountType = (value: ACCTYPE | null) => {
            formik.setFieldValue('accttype', value);
          };
          return (
            <>
              <PaymentMethodSelector
                achEnabled
                ccEnabled
                onSelectPaymentMethod={setPaymentMethod}
                paymentMethod={paymentMethod}
              />
              <CustomPaymentFormElements.FormGroup>
                {paymentDetailsInfo}
              </CustomPaymentFormElements.FormGroup>
              {paymentType === PaymentType.ACH && (
                <CustomPaymentFormElements.FormGroup>
                  <CustomPaymentFormElements.Label htmlFor="accountType">
                    Account Type
                  </CustomPaymentFormElements.Label>
                  <CustomPaymentFormElements.Select
                    onChange={setAccountType}
                    value={formik.values.accttype}
                    data={accountTypeData}
                    searchable={false}
                    cleanable={false}
                  />
                </CustomPaymentFormElements.FormGroup>
              )}
              <CustomPaymentFormElements.FormGroup>
                <iframe
                  title="tokenFram"
                  id="tokenFrame"
                  name="tokenFrame"
                  src={iframeUrl.toString()}
                  width="100%"
                  height={
                    paymentMethod === PAYMENT_METHODS.CREDIT_OR_DEBIT__CARD
                      ? '170'
                      : '40'
                  }
                  frameBorder="0"
                  scrolling="no"
                />
              </CustomPaymentFormElements.FormGroup>
            </>
          );
        }}
      </CustomPaymentForm>
    </GeneralPageLayout>
  );
};
